import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { useLanguage } from "@/reusable_sections/Header";
const fallbackPosts = [{
  id: "fallback-1",
  title: {
    en: "Navigating Haitian Legal Frameworks: Cross-Border Know-How",
    fr: "Naviguer dans les cadres juridiques haïtiens : Savoir-faire transfrontalier",
    ht: "Navige nan kad legal ayisyen : Konesans transfontyè"
  },
  excerpt: {
    en: "An in-depth analysis of Haitian civil code standards, international compliance nuances, and foreign legal documentation protocols.",
    fr: "Une analyse approfondie des normes du code civil haïtien, des nuances de conformité internationale et des protocoles de documentation juridique étrangère.",
    ht: "Yon analiz apwofondi sou estanda kòd sivil ayisyen, nuans konfòmite entènasyonal, ak pwotokòl dokimantasyon legal etranje."
  },
  date: "2025.03.18",
  readTime: "12 MIN",
  category: {
    en: "HAITIAN LAW",
    fr: "DROIT HAÏTIEN",
    ht: "LWA AYISYEN"
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1486499/img/legal_consultation_desk.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1486499/img/legal_consultation_desk.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1486499/img/legal_consultation_desk.png-wvc-sizes",
  link: "/consultation"
}, {
  id: "fallback-2",
  title: {
    en: "Real Estate & Title Compliance: Strategic Cross-Border Advisory",
    fr: "Conformité immobilière et titres : Conseil stratégique transfrontalier",
    ht: "Konfòmite imobilye ak tit : Konsèy estratejik transfontyè"
  },
  excerpt: {
    en: "Essential procedural insights, title due diligence, and regulatory compliance standards for cross-border real estate acquisitions and assets.",
    fr: "Perspectives procédurales essentielles, contrôle préalable des titres et normes de conformité réglementaire pour les acquisitions et actifs immobiliers transfrontaliers.",
    ht: "Konesans pwosedirèl esansyèl, verifikasyon tit, ak estanda konfòmite reglemantè pou akizisyon ak byen imobilye transfontyè."
  },
  date: "2025.02.27",
  readTime: "6 MIN",
  category: {
    en: "REAL ESTATE",
    fr: "IMMOBILIER",
    ht: "IMOBILYE"
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1486499/img/immigration_documents_stack.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1486499/img/immigration_documents_stack.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1486499/img/immigration_documents_stack.png-wvc-sizes",
  link: "/consultation"
}, {
  id: "fallback-3",
  title: {
    en: "Multi-Discipline Advisory: Tax, Publishing & Consulting Standards",
    fr: "Conseil multidisciplinaire : Normes fiscales, d'édition et de conseil",
    ht: "Konsèy miltidisiplinè : Estanda fiskal, piblikasyon ak konsiltasyon"
  },
  excerpt: {
    en: "Examining integrated frameworks across international taxation, intellectual property publishing, and certified foreign legal consulting.",
    fr: "Examen des cadres intégrés en matière de fiscalité internationale, d'édition de propriété intellectuelle et de conseil juridique étranger certifié.",
    ht: "Egzamine kad entegre nan domèn taksasyon entènasyonal, piblikasyon pwopriyete entèlektyèl, ak konsiltasyon legal etranje sètifye."
  },
  date: "2025.02.04",
  readTime: "8 MIN",
  category: {
    en: "STRATEGIC ADVISORY",
    fr: "CONSEIL STRATÉGIQUE",
    ht: "KONSÈY ESTRATEJIK"
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1486499/img/legal_certification_seal.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1486499/img/legal_certification_seal.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1486499/img/legal_certification_seal.png-wvc-sizes",
  link: "/consultation"
}];
function formatDate(dateString?: string) {
  if (!dateString) return "";
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}.${month}.${day}`;
  } catch {
    return "";
  }
}
function estimateReadTime(content?: string) {
  if (!content) return "5 MIN";
  const text = content.replace(/<[^>]+>/g, " ");
  const words = text.trim().split(/\s+/).length;
  const minutes = Math.max(1, Math.round(words / 200));
  return `${minutes} MIN`;
}
function stripHtml(html?: string) {
  if (!html) return "";
  return html.replace(/<[^>]+>/g, "").trim();
}
function BenchReportsConsumer() {
  const {
    lang
  } = useLanguage();
  const {
    posts,
    loading,
    error
  } = usePosts();
  const readLabel = lang === "fr" ? "LIRE" : lang === "ht" ? "LI" : "READ";
  if (loading) {
    return <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-border">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className={`bg-background ${i === 0 ? "lg:col-span-2 lg:row-span-2" : ""}`}>
            <div className="aspect-[16/10] bg-muted/30 animate-pulse" />
            <div className="p-6 space-y-4">
              <div className="h-3 w-32 bg-muted/30 animate-pulse" />
              <div className="h-8 w-3/4 bg-muted/30 animate-pulse" />
              <div className="h-4 w-full bg-muted/30 animate-pulse" />
            </div>
          </div>)}
      </div>;
  }
  const localizedFallback = fallbackPosts.map(p => ({
    id: p.id,
    title: p.title[lang],
    excerpt: p.excerpt[lang],
    date: p.date,
    readTime: p.readTime,
    category: p.category[lang],
    image: p.image,
    srcset: p.srcset,
    sizes: p.sizes,
    link: p.link
  }));
  const displayPosts = !error && posts && posts.length > 0 ? posts.slice(0, 3).map(post => ({
    id: post.id,
    title: post.title,
    excerpt: stripHtml(post.excerpt).slice(0, 180),
    date: formatDate(post.date),
    readTime: estimateReadTime(post.content || post.excerpt),
    category: post.categories && post.categories.length > 0 ? post.categories[0].name.toUpperCase() : "LEGAL ANALYSIS",
    image: post.featuredImage || fallbackPosts[0].image,
    srcset: "",
    sizes: "",
    link: post.link || "/consultation",
    isWpPost: true
  })) : localizedFallback.map(p => ({
    ...p,
    isWpPost: false
  }));
  while (displayPosts.length < 3) {
    const idx = displayPosts.length;
    displayPosts.push({
      ...localizedFallback[idx],
      isWpPost: false
    } as any);
  }
  const featured = displayPosts[0];
  const secondary = displayPosts.slice(1, 3);
  return <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Link to={featured.link || "/consultation"} data-index={0} className="group lg:col-span-2 lg:row-span-2 border border-border rounded-md overflow-hidden bg-card flex flex-col">
        <div className="relative w-full overflow-hidden aspect-[16/10]">
          {(featured as any).isWpPost ? <img src={featured.image} alt={featured.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.03]" /> : <img src={featured.image} data-wvc-srcset={(featured as any).srcset} data-wvc-sizes={(featured as any).sizes} alt={featured.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.03]" />}
          <div className="absolute top-0 left-0 w-6 h-6 border-t border-l border-primary opacity-70" />
          <div className="absolute top-0 right-0 w-6 h-6 border-t border-r border-primary opacity-70" />
          <div className="absolute bottom-0 left-0 w-6 h-6 border-b border-l border-primary opacity-70" />
          <div className="absolute bottom-0 right-0 w-6 h-6 border-b border-r border-primary opacity-70" />
        </div>
        <div className="flex flex-col gap-5 p-6 md:p-8 flex-1">
          <div className="inline-flex w-fit items-center gap-2 bg-primary px-3 py-1.5">
            <span className="font-mono text-[10px] sm:text-xs tracking-[0.2em] text-primary-foreground">
              / {featured.category}
            </span>
          </div>
          <h3 className="font-default text-2xl sm:text-3xl md:text-4xl lg:text-[36px] leading-[1.1] font-semibold text-foreground transition-colors duration-300 group-hover:text-primary">
            {featured.title}
          </h3>
          <p className="text-[15px] leading-relaxed text-muted-foreground max-w-2xl">
            {featured.excerpt}
          </p>
          <div className="mt-auto pt-4 flex items-center justify-between border-t border-border/60">
            <div className="flex items-center gap-4 font-mono text-xs tracking-wider text-muted-foreground">
              <span>{featured.date}</span>
              <span className="text-border">/</span>
              <span>
                {featured.readTime} {readLabel}
              </span>
            </div>
            <span className="font-mono text-xs tracking-[0.2em] text-foreground inline-flex items-center gap-1">
              {readLabel}
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </div>
        </div>
      </Link>

      {secondary.map((post, idx) => <Link key={post.id} to={post.link || "/consultation"} data-index={idx + 1} className="group border border-border rounded-md overflow-hidden bg-card flex flex-col">
          <div className="relative w-full overflow-hidden aspect-[4/3]">
            {(post as any).isWpPost ? <img src={post.image} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.03]" /> : <img src={post.image} data-wvc-srcset={(post as any).srcset} data-wvc-sizes={(post as any).sizes} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.03]" />}
          </div>
          <div className="flex flex-col gap-3 p-5 md:p-6 flex-1">
            <span className="font-mono text-[10px] sm:text-xs tracking-[0.2em] text-[color:var(--chart-2)]">
              / {post.category}
            </span>
            <h3 className="font-default text-lg sm:text-xl leading-tight font-semibold text-foreground transition-colors duration-300 group-hover:text-primary">
              {post.title}
            </h3>
            <div className="mt-auto pt-3 flex items-center gap-3 font-mono text-[11px] tracking-wider text-muted-foreground border-t border-border/60">
              <span>{post.date}</span>
              <span className="text-border">/</span>
              <span>
                {post.readTime} {readLabel}
              </span>
            </div>
          </div>
        </Link>)}
    </div>;
}
export default function LegalInsightsUpdates() {
  const {
    lang
  } = useLanguage();
  const content = {
    sectionTag: {
      en: "06 / INSIGHTS",
      fr: "06 / INSIGHTS",
      ht: "06 / ENFOMASYON"
    },
    archive: {
      en: "/ARCHIVE",
      fr: "/ARCHIVE",
      ht: "/ACHIV"
    },
    guidance: {
      en: "SN/06 — EXPERT GUIDANCE",
      fr: "SN/06 — CONSEILS EXPERTS",
      ht: "SN/06 — DIREKTIV EKSPÈ"
    },
    heading: {
      en: "Clarity across every Service",
      fr: "Clarté dans chaque service",
      ht: "Klate nan chak sèvis"
    },
    consultationTag: {
      en: "/ CONSULTATION",
      fr: "/ CONSULTATION",
      ht: "/ KONSILTASYON"
    },
    consultationDesc: {
      en: "Expert insights across immigration, tax, real estate, publishing, and international legal matters.",
      fr: "Analyses d'experts en matière d'immigration, de fiscalité, d'immobilier, d'édition et de droit international.",
      ht: "Analiz ekspè sou zafè imigrasyon, taks, imobilye, piblikasyon ak zafè legal entènasyonal."
    },
    filedUnder: {
      en: "FILED UNDER IMMIGRATION / TAX / REAL ESTATE / PUBLISHING / HAITIAN LAW",
      fr: "CLASSÉ SOUS IMMIGRATION / FISCALITÉ / IMMOBILIER / ÉDITION / DROIT HAÏTIEN",
      ht: "KLASE ANBA IMIIGRASYON / TAKS / IMOBILIÈ / PIBLIKASYON / LWA AYISYEN"
    },
    ctaButton: {
      en: "BOOK A CONSULTATION",
      fr: "RÉSERVER UNE CONSULTATION",
      ht: "REZÈVE YON KONSILTASYON"
    }
  };
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="4976" id="legal-insights-updates" className="bg-background text-foreground relative overflow-hidden py-14 md:py-20 lg:py-24 px-5 sm:px-8 lg:px-12">
      <div className="pointer-events-none absolute top-8 left-8 w-4 h-4">
        <div className="absolute top-1/2 left-0 right-0 h-px bg-border" />
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-border" />
      </div>
      <div className="pointer-events-none absolute top-8 right-8 w-4 h-4">
        <div className="absolute top-1/2 left-0 right-0 h-px bg-border" />
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-border" />
      </div>

      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between mb-6 md:mb-8 font-mono text-xs tracking-[0.2em]">
          <div className="flex items-center gap-3 text-muted-foreground">
            <span className="text-primary">§</span>
            <span>{content.sectionTag[lang]}</span>
          </div>
          <Link to="#" className="text-foreground hover:text-primary transition-colors duration-300 inline-flex items-center gap-2">
            <span>{content.archive[lang]}</span>
            <span aria-hidden="true">→</span>
          </Link>
        </div>

        <div className="mb-8 md:mb-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 lg:gap-8">
          <div className="max-w-4xl">
            <div className="font-mono text-[11px] tracking-[0.25em] text-muted-foreground mb-3 flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-primary" />
              <span>{content.guidance[lang]}</span>
            </div>
            <h2 className="font-default text-3xl md:text-5xl font-bold tracking-tight text-foreground leading-tight">
              {content.heading[lang]}.
            </h2>
          </div>
          <div className="hidden lg:block font-mono text-xs tracking-wider text-muted-foreground max-w-[240px] text-right">
            <div className="border-l border-border pl-4">
              <span className="block text-foreground mb-1">
                {content.consultationTag[lang]}
              </span>
              <span>{content.consultationDesc[lang]}</span>
            </div>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <BenchReportsConsumer />
        </PostsProvider>

        <div className="mt-10 md:mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-6 border-t border-border">
          <div className="font-mono text-xs tracking-[0.2em] text-muted-foreground">
            <span className="text-primary">●</span>
            <span className="ml-3">{content.filedUnder[lang]}</span>
          </div>
          <Link to="#">
            <Button variant="outline" className="font-mono text-xs tracking-[0.2em] border-border bg-transparent hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors duration-300 h-11 px-6">
              {content.ctaButton[lang]}
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}